import { Link } from '@tanstack/react-router';
import { linkTextClasses, Panel, StatCard, StatCardList } from '@evogard/ui';
import { count } from '@evogard/domain';
import { Age, BreakerBadge, useErpHealth } from './common.js';

/**
 * O ERP no início da interna: a esteira está viva, e quando ela passou.
 *
 * ## Os mesmos números da tela, e nenhuma segunda consulta
 *
 * Lê `GET /erp/health` pela mesma chave da tela de saúde — a consulta é uma só,
 * compartilhada pelo cache. Uma rota própria "resumida" para o painel seria uma
 * segunda fonte para o mesmo fato, e as duas divergiriam no primeiro dia em que
 * alguém corrigisse uma delas. Também custaria uma chamada a mais numa
 * integração limitada a uma a cada quatro segundos.
 *
 * ## O painel não fabrica número
 *
 * Enquanto a consulta está no ar, o lugar do número é esqueleto — nunca zero.
 * Se a leitura falha, o painel **diz** que não conseguiu ler, e não desenha uma
 * esteira parada que ninguém mediu: "a integração está de pé" e "não sabemos
 * como ela está" são coisas diferentes, e o cartão que as desenhasse iguais
 * seria pior do que cartão nenhum.
 *
 * ## E "nunca sincronizou" tem lugar de destaque aqui
 *
 * É o único número deste painel que pede ação de alguém. Entidade que nunca
 * passou não é entidade vazia: é uma parte do espelho que ninguém preencheu, e
 * quem lê dela recebe menos do que pensa estar recebendo.
 */
export function ErpPanel() {
  const query = useErpHealth();
  const health = query.data;

  const completed =
    health?.syncs.flatMap((s) => (s.estado === 'concluida' ? [s] : [])) ?? [];
  const moreRecent = completed
    .map((s) => s.completedAt)
    .sort()
    .at(-1);
  const never = health?.syncs.filter((s) => s.estado === 'nunca').length ?? 0;

  return (
    <Panel
      title="ERP (espelho)"
      descricao="A esteira que mantém o espelho desta base."
      actions={
        <Link to="/erp/saude" className={linkTextClasses('text-xs')}>
          Ver saúde
        </Link>
      }
      footer={
        /*
         * O disjuntor não é número, e por isso não é cartão: ele é um estado com
         * cor, símbolo e palavra, que é o que `BreakerBadge` desenha. Pô-lo num
         * `StatCard` obrigaria a escolher um número para representá-lo, e o
         * número não existe.
         */
        query.isError || !health ? undefined : (
          <BreakerBadge estado={health.breaker.estado} />
        )
      }
    >
      {query.isError ? (
        <p className="text-sm text-texto-suave">
          Não foi possível ler o estado da integração agora. A tela de saúde continua
          acessível.
        </p>
      ) : (
        <StatCardList>
          <StatCard
            icon="erp"
            label="Entidades espelhadas"
            value={health?.syncs.length}
            support={
              /*
               * String vazia, e não `undefined`, enquanto a consulta está no ar:
               * é assim que o cartão sabe que este número TEM linha de apoio, e
               * o esqueleto nasce com as duas barras em vez de mudar de altura
               * quando o dado chega.
               */
              health === undefined ? (
                ''
              ) : moreRecent ? (
                /*
                 * Sem nenhuma passagem concluída não há o que datar, e uma
                 * data qualquer aqui seria o pior tipo de número: plausível.
                 */
                <>
                  última passagem <Age iso={moreRecent} />
                </>
              ) : (
                'nenhuma concluiu uma passagem ainda'
              )
            }
          />
          {/*
           * `alerta` porque este é o único número deste painel que pede ação de
           * alguém: entidade que nunca passou não é entidade vazia, é uma parte
           * do espelho que ninguém preencheu. Zero não pede nada, e por isso
           * zero não acende — âmbar permanente é âmbar que se para de ver.
           */}
          <StatCard
            icon="atencao"
            label="Nunca sincronizaram"
            value={health === undefined ? undefined : never}
            tone={never > 0 ? 'alerta' : 'neutro'}
            support={
              health === undefined
                ? ''
                : never === 0
                  ? 'todas já passaram ao menos uma vez'
                  : count(
                      never,
                      'entidade sem nenhuma passagem',
                      'entidades sem nenhuma passagem',
                    )
            }
          />
        </StatCardList>
      )}
    </Panel>
  );
}
